import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * AdminCursor — early-90s computer arrow pointer, restyled for The Relative Gallery.
 *
 * - Pixel-shape arrow (hot-spot at the tip — top-left corner of the SVG)
 * - White fill, dark outline (Mac/Windows-era classic)
 * - Subtle cyan glow on interactive elements
 * - Click pulse remains for tactile feedback
 * - Disabled on touch devices (native cursor falls back via index.css media query)
 *
 * Scope: .admin-root only.
 */
export default function AdminCursor() {
  const pointerRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [pressed, setPressed] = useState(false);
  const [clicks, setClicks] = useState([]);

  const visibleRef = useRef(false);
  const hoveringRef = useRef(false);
  const touchRef = useRef(false);

  useEffect(() => {
    const isTouch =
      window.matchMedia?.('(pointer: coarse)').matches ||
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0;
    if (isTouch) {
      touchRef.current = true;
      return;
    }

    const SELECTOR =
      'button, a, input, textarea, select, [role="button"], [data-clickable="true"]';

    const move = (e) => {
      const el = pointerRef.current;
      if (el) {
        // Hot-spot is the SVG top-left (no translate(-50%,-50%))
        el.style.transform = `translate3d(${e.clientX}px, ${e.clientY}px, 0)`;
      }
      if (!visibleRef.current) {
        visibleRef.current = true;
        setVisible(true);
      }

      const target = document.elementFromPoint(e.clientX, e.clientY);
      const isHover = !!(target && target.closest && target.closest(SELECTOR));
      if (isHover !== hoveringRef.current) {
        hoveringRef.current = isHover;
        setHovering(isHover);
      }
    };

    const leave = () => {
      visibleRef.current = false;
      setVisible(false);
    };
    const enter = () => {
      visibleRef.current = true;
      setVisible(true);
    };
    const down = () => setPressed(true);
    const up = () => setPressed(false);
    const click = (e) => {
      const id = Date.now() + Math.random();
      setClicks((c) => [...c, { id, x: e.clientX, y: e.clientY }]);
      setTimeout(() => setClicks((c) => c.filter((p) => p.id !== id)), 280);
    };

    window.addEventListener('mousemove', move, { passive: true });
    window.addEventListener('mousedown', down);
    window.addEventListener('mouseup', up);
    window.addEventListener('click', click);
    document.addEventListener('mouseleave', leave);
    document.addEventListener('mouseenter', enter);

    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mousedown', down);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('click', click);
      document.removeEventListener('mouseleave', leave);
      document.removeEventListener('mouseenter', enter);
    };
  }, []);

  if (touchRef.current) return null;

  return (
    <>
      {/* Main arrow pointer — direct-transform, zero lag.
          z-index sits ABOVE any modal/dialog (which we cap at z-9999)
          so the custom cursor stays visible over confirmation popups,
          the schedule modal, the preview overlay, etc. Without this
          the modal backdrop hides the cursor and the user is
          effectively "blind" while choosing a button. */}
      <div
        ref={pointerRef}
        data-testid="admin-cursor"
        className="fixed top-0 left-0 pointer-events-none z-[100000]"
        style={{
          width: 18,
          height: 22,
          opacity: visible ? 1 : 0,
          transition: 'opacity 120ms ease',
          willChange: 'transform',
        }}
      >
        <svg
          viewBox="0 0 18 22"
          width="18"
          height="22"
          xmlns="http://www.w3.org/2000/svg"
          style={{
            transform: `scale(${pressed ? 0.92 : hovering ? 1.06 : 1})`,
            transformOrigin: '1px 1px',
            transition: 'transform 90ms cubic-bezier(0.2, 0.8, 0.2, 1)',
            filter: hovering
              ? 'drop-shadow(0 0 5px rgba(144,224,239,0.55))'
              : 'drop-shadow(0 1px 1.2px rgba(0,0,0,0.65))',
          }}
        >
          {/*
           * Classic arrow shape — outline + tail.
           * Path traces from the tip (1,1) down/right to form the
           * pointer body and tail, faithful to early Mac/Win pointers.
           */}
          <path
            d="M1 1 L1 16.5 L4.6 13.0 L7.0 18.6 L9.4 17.6 L7.0 12.0 L12.0 12.0 Z"
            fill={hovering ? '#FFFFFF' : '#F5F5F5'}
            stroke={hovering ? '#90E0EF' : '#0a0a0a'}
            strokeWidth="1.2"
            strokeLinejoin="miter"
            strokeLinecap="butt"
          />
          {/* Subtle inner highlight at the tip — keeps the retro chunky look readable on dark UI */}
          <path
            d="M2.4 3.1 L2.4 12.5"
            stroke="rgba(0,0,0,0.18)"
            strokeWidth="0.6"
            strokeLinecap="round"
          />
        </svg>
      </div>

      {/* Click pulse rings */}
      <AnimatePresence>
        {clicks.map((c) => (
          <motion.span
            key={c.id}
            className="fixed pointer-events-none z-[99999]"
            initial={{ opacity: 0.85, scale: 0.25 }}
            animate={{ opacity: 0, scale: 1.7 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.2, 0.8, 0.2, 1] }}
            style={{
              left: c.x - 16,
              top: c.y - 16,
              width: 32,
              height: 32,
              borderRadius: '50%',
              border: '1.2px solid #90E0EF',
              boxShadow: '0 0 12px rgba(144,224,239,0.45)',
            }}
          />
        ))}
      </AnimatePresence>
    </>
  );
}
